/**
 * ManualLocationInput Component
 *
 * Allows users to manually enter location via address search or coordinates
 * when automatic geolocation fails or is unavailable.
 */

import { useState, useCallback } from 'react'
import type { GeoLocation } from '@/lib/types'
import type { LocationSource } from '@/hooks/useGeolocation'

export interface ManualLocationInputProps {
  currentLocation: GeoLocation | null
  locationSource: LocationSource | null
  error: GeolocationPositionError | null
  isLoading: boolean
  onSetManualLocation: (location: GeoLocation) => void
  onClearManualLocation: () => void
  onRetryAutoLocation: () => void
}

interface GeocodingResult {
  display_name: string
  lat: string
  lon: string
}

/**
 * Geocode an address using OpenStreetMap Nominatim (free, no API key)
 */
async function geocodeAddress(address: string): Promise<GeocodingResult[]> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=5`,
    {
      headers: {
        'User-Agent': 'CrowdTalkie/1.0 (https://crowdtalkie.com)',
      },
    }
  )
  if (!response.ok) throw new Error('Geocoding failed')
  return response.json()
}

// Common locations for quick selection (useful for testing)
const PRESET_LOCATIONS: { name: string; location: GeoLocation }[] = [
  {
    name: 'London (Trafalgar Square)',
    location: { latitude: 51.508, longitude: -0.128 },
  },
  {
    name: 'New York (Times Square)',
    location: { latitude: 40.758, longitude: -73.9855 },
  },
  {
    name: 'San Francisco (Union Square)',
    location: { latitude: 37.788, longitude: -122.407 },
  },
  {
    name: 'Berlin (Brandenburg Gate)',
    location: { latitude: 52.5163, longitude: 13.3777 },
  },
  {
    name: 'Tokyo (Shibuya)',
    location: { latitude: 35.6595, longitude: 139.7004 },
  },
]

export function ManualLocationInput({
  currentLocation,
  locationSource,
  error,
  isLoading,
  onSetManualLocation,
  onClearManualLocation,
  onRetryAutoLocation,
}: ManualLocationInputProps) {
  const [showManualInput, setShowManualInput] = useState(false)
  const [inputMode, setInputMode] = useState<'address' | 'coordinates'>('address')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)
  const [addressQuery, setAddressQuery] = useState('')
  const [addressResults, setAddressResults] = useState<GeocodingResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setInputError(null)

      const lat = parseFloat(latitude)
      const lng = parseFloat(longitude)

      if (isNaN(lat) || lat < -90 || lat > 90) {
        setInputError('Latitude must be between -90 and 90')
        return
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        setInputError('Longitude must be between -180 and 180')
        return
      }

      onSetManualLocation({
        latitude: lat,
        longitude: lng,
        accuracy: 1,
        timestamp: new Date(),
      })
      setShowManualInput(false)
    },
    [latitude, longitude, onSetManualLocation]
  )

  const handlePresetSelect = useCallback(
    (location: GeoLocation) => {
      onSetManualLocation(location)
      setShowManualInput(false)
    },
    [onSetManualLocation]
  )

  const handleClearManual = useCallback(() => {
    onClearManualLocation()
    setLatitude('')
    setLongitude('')
    setAddressQuery('')
    setAddressResults([])
  }, [onClearManualLocation])

  const handleAddressSearch = useCallback(async () => {
    if (!addressQuery.trim()) return

    setIsSearching(true)
    setInputError(null)
    setAddressResults([])

    try {
      const results = await geocodeAddress(addressQuery)
      if (results.length === 0) {
        setInputError('No results found. Try a different address.')
      } else {
        setAddressResults(results)
      }
    } catch (err) {
      setInputError('Search failed. Please try again.')
      console.error('Geocoding error:', err)
    } finally {
      setIsSearching(false)
    }
  }, [addressQuery])

  const handleSelectAddress = useCallback(
    (result: GeocodingResult) => {
      onSetManualLocation({
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        accuracy: 10, // Address-based, ~10m accuracy
        timestamp: new Date(),
      })
      setShowManualInput(false)
      setAddressQuery('')
      setAddressResults([])
    },
    [onSetManualLocation]
  )

  // Get location source indicator
  const getSourceBadge = () => {
    if (!locationSource) return null

    const badges: Record<LocationSource, { label: string; color: string; icon: string }> = {
      gps: { label: 'GPS', color: 'bg-green-500/20 text-green-400', icon: '📍' },
      ip: { label: 'IP (approx)', color: 'bg-yellow-500/20 text-yellow-400', icon: '🌐' },
      manual: { label: 'Manual', color: 'bg-blue-500/20 text-blue-400', icon: '✏️' },
    }

    const badge = badges[locationSource]
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    )
  }

  return (
    <div className="space-y-4">
      {/* Current Location Status */}
      <div className="p-4 rounded-lg glass-panel border border-white/10 relative overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-3 text-white/60">
            <span className="animate-pulse text-primary">📍</span>
            <span className="font-mono text-sm tracking-widest animate-pulse">ACQUIRING_SIGNAL...</span>
          </div>
        ) : currentLocation ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-400">
                <span className="text-xs">📍</span>
                <span className="font-bold text-xs uppercase tracking-widest">Signal Locked</span>
                {currentLocation.accuracy && (
                  <span className="text-[10px] text-white/40 font-mono bg-white/5 px-1.5 py-0.5 rounded">
                    ±{Math.round(currentLocation.accuracy)}m
                  </span>
                )}
              </div>
              {getSourceBadge()}
            </div>
            <div className="text-lg font-mono text-white tracking-wider flex items-center gap-3 bg-black/40 p-2 rounded border border-white/5">
              <span>{currentLocation.latitude.toFixed(6)}</span>
              <span className="text-white/20">|</span>
              <span>{currentLocation.longitude.toFixed(6)}</span>
            </div>
            <div className="flex gap-4 pt-2 border-t border-white/5">
              {locationSource === 'manual' && (
                <button
                  type="button"
                  onClick={handleClearManual}
                  className="text-[10px] text-primary hover:text-primary/80 uppercase tracking-wider font-bold transition-colors"
                >
                  [ RESET_AUTO_TRACKING ]
                </button>
              )}
              {locationSource === 'ip' && (
                <div className="flex items-center justify-between w-full">
                  <span className="text-[10px] text-yellow-400/80 uppercase tracking-wide">
                    ⚠ Low Precision Signal
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowManualInput(true)}
                    className="px-3 py-1 text-[10px] rounded bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-colors uppercase font-bold tracking-wider"
                  >
                    Override Coords
                  </button>
                </div>
              )}
              {locationSource === 'gps' && (
                <button
                  type="button"
                  onClick={() => setShowManualInput(true)}
                  className="text-[10px] text-white/30 hover:text-white/60 uppercase tracking-wider transition-colors ml-auto"
                >
                  [ MANUAL_OVERRIDE ]
                </button>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="space-y-3">
            <div className="text-red-400 flex items-start gap-2 bg-red-500/10 p-3 rounded border border-red-500/20">
              <span>⚠️</span>
              <span className="text-xs font-mono">{error.message}</span>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={onRetryAutoLocation}
                className="text-xs text-primary hover:text-primary/80 uppercase tracking-wider font-bold"
              >
                [ RETRY_SIGNAL ]
              </button>
              <span className="text-white/10">|</span>
              <button
                type="button"
                onClick={() => setShowManualInput(!showManualInput)}
                className="text-xs text-primary hover:text-primary/80 uppercase tracking-wider font-bold"
              >
                [ MANUAL_ENTRY ]
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-4 flex-wrap">
            <button
              type="button"
              onClick={onRetryAutoLocation}
              className="text-xs text-primary hover:text-primary/80 uppercase tracking-wider font-bold"
            >
              [ ENABLE_SENSORS ]
            </button>
            <span className="text-white/10">|</span>
            <button
              type="button"
              onClick={() => setShowManualInput(!showManualInput)}
              className="text-xs text-primary hover:text-primary/80 uppercase tracking-wider font-bold"
            >
              [ MANUAL_ENTRY ]
            </button>
          </div>
        )}
      </div>

      {/* Manual Input Form */}
      {showManualInput && (
        <div className="p-4 rounded-lg glass-panel border border-white/10 space-y-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-primary uppercase tracking-widest">Manual Coordinates</h4>
            <button
              onClick={() => {
                setShowManualInput(false)
                setAddressResults([])
                setInputError(null)
              }}
              className="text-white/20 hover:text-white"
            >
              ✕
            </button>
          </div>

          {/* Input Mode Tabs */}
          <div className="flex p-0.5 bg-black/40 rounded border border-white/10">
            <button
              type="button"
              onClick={() => setInputMode('address')}
              className={`flex-1 px-3 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-all ${inputMode === 'address'
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-white/30 hover:text-white/50'
                }`}
            >
              Address Lookup
            </button>
            <button
              type="button"
              onClick={() => setInputMode('coordinates')}
              className={`flex-1 px-3 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-all ${inputMode === 'coordinates'
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-white/30 hover:text-white/50'
                }`}
            >
              Raw Coords
            </button>
          </div>

          {/* Address Search */}
          {inputMode === 'address' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={addressQuery}
                  onChange={(e) => setAddressQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddressSearch()
                    }
                  }}
                  placeholder="ENTER_TARGET_LOCATION..."
                  className="flex-1 px-3 py-2 text-sm rounded bg-black/40 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:neon-border font-mono uppercase"
                />
                <button
                  type="button"
                  onClick={handleAddressSearch}
                  disabled={isSearching || !addressQuery.trim()}
                  className="px-4 py-2 text-xs rounded bg-primary/20 text-primary border border-primary/50 font-bold uppercase tracking-wider hover:bg-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? 'SCANNING...' : 'SCAN'}
                </button>
              </div>

              {/* Search Results */}
              {addressResults.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1 scrollbar-hide">
                  {addressResults.map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectAddress(result)}
                      className="w-full text-left px-3 py-2 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-colors border border-transparent hover:border-white/10"
                    >
                      <div className="line-clamp-2 font-mono uppercase">{result.display_name}</div>
                    </button>
                  ))}
                </div>
              )}

              {inputError && (
                <p className="text-xs text-red-400 font-mono bg-red-500/10 p-2 rounded border border-red-500/20">
                  ⚠ {inputError}
                </p>
              )}

              <p className="text-[10px] text-white/30 uppercase tracking-wider font-mono">
                // SEARCHING OPENSTREETMAP DATABASE //
              </p>
            </div>
          )}

          {/* Coordinate Input */}
          {inputMode === 'coordinates' && (
            <>
              {/* Preset Locations */}
              <div className="space-y-2">
                <p className="text-[10px] text-white/30 uppercase tracking-wider">Target Presets:</p>
                <div className="flex flex-wrap gap-2">
                  {PRESET_LOCATIONS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => handlePresetSelect(preset.location)}
                      className="text-[10px] px-2 py-1 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors uppercase font-mono"
                    >
                      {preset.name.split(' (')[0]}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 pt-2 border-t border-white/5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-primary uppercase tracking-wider mb-1">Lat</label>
                    <input
                      type="text"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      placeholder="00.0000"
                      className="w-full px-2 py-2 text-sm rounded bg-black/40 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:neon-border font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-primary uppercase tracking-wider mb-1">Long</label>
                    <input
                      type="text"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      placeholder="00.0000"
                      className="w-full px-2 py-2 text-sm rounded bg-black/40 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:neon-border font-mono"
                    />
                  </div>
                </div>

                {inputError && (
                  <p className="text-xs text-red-400 font-mono bg-red-500/10 p-2 rounded border border-red-500/20">
                    ⚠ {inputError}
                  </p>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded bg-primary/20 text-primary border border-primary/50 font-bold uppercase tracking-widest hover:bg-primary/30 hover:shadow-[0_0_15px_rgba(242,13,128,0.3)] transition-all"
                >
                  Set Coordinates
                </button>
              </form>
            </>
          )}
        </div>
      )}

    </div>
  )
}
